import { staticFormApiConnectorLegacy } from 'apiConnector/form/static';
import type { ReloadFormRequest } from 'epic/common-form/ReloadFormRequest';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { staticFormReloadedThunk } from 'state/static/staticForm.thunks';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

export interface ReloadStaticFormBasketRequest extends ReloadFormRequest {
  tabId: string;
  basket: string[];
}

export interface FetchFormFromAdvisoryResult {
  getFormFromAdvisoryResultByIdType(request: ReloadStaticFormBasketRequest): Observable<StaticFormResult>;
}

export function fetchStaticFormFromAdvisoryResultEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, state$, { oldHttpClient, selectors: { isUserPreferencesPrivateMode } }) =>
    action$.pipe(
      ofType('ADVISORY_RESULT_FORM_LOAD_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ newTabId, quoteId, basket }, state]) => {
        const tabRemoved$ = tabRemoved(action$, newTabId, state$);

        const tradeDate = getTodayStringInLocalTimezone();
        const isPrivateMode = isUserPreferencesPrivateMode(state);
        const getForm$ = staticFormApiConnectorLegacy(oldHttpClient).getFormFromAdvisoryResultByIdType({
          tabId: newTabId,
          id: quoteId,
          idType: IdType.QuoteId,
          basket,
          tradeDate,
          isPrivateMode,
        });

        return getForm$.pipe(
          takeUntil(tabRemoved$),
          mergeMap(
            ({
              values,
              blocksMetadata,
              fieldsMetadata,
              version,
              actions,
              quoteQuery,
              errors,
              negotiationId,
              productView,
              mustForceAdvancedMode,
              allowedRequests,
            }) =>
              of(
                staticFormReloadedThunk(
                  newTabId,
                  productView,
                  negotiationId,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  version,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                ),
              ),
          ),
          catchError(error =>
            of(
              commonFormActions.formReloadFailed(
                'static',
                newTabId,
                error.message || 'load form from basket advice failed',
              ),
            ),
          ),
          timeoutWith(
            timeoutInMillis,
            of(commonFormActions.formReloadFailed('static', newTabId, 'Timeout')),
            appScheduler,
          ),
        );
      }),
    );
}
